import type { VipReview } from '../types';

/**
 * Mock review pools keyed by VipProfile.id. Each provider has 2–4
 * reviews. Reviews are deliberately understated, lowercase, mate-at-1am
 * rather than star-rated dating-app saccharine.
 *
 * Reminder: HOTTR does not verify these encounters. They are
 * user-submitted testimonials, surfaced with that caveat. See
 * docs/HOTTR_VIP_PRODUCT_RULES.md.
 */
export const mockVipReviews: Record<string, VipReview[]> = {
  vip_01: [
    {
      id: 'rev_01_01',
      authorHandle: 'mark.r',
      authorCity: 'Melbourne',
      submittedAt: '2026-04-12',
      rating: 5,
      title: 'a long, slow night.',
      body: 'Exactly the room you hope you booked. Conversation worth the second drink. Read the brief.',
      providerAcknowledged: true,
    },
    {
      id: 'rev_01_02',
      authorHandle: 'j_quiet',
      authorCity: 'Sydney',
      submittedAt: '2026-02-19',
      rating: 5,
      body: 'Discreet, sharp, on time. Made the hour feel like three.',
    },
    {
      id: 'rev_01_03',
      authorHandle: 'd_v',
      authorCity: 'Melbourne',
      submittedAt: '2025-12-04',
      rating: 4,
      title: 'better than expected.',
      body: 'Old hotel bar. Late dinner. He carried the room without trying. Would book again.',
    },
  ],
  vip_02: [
    {
      id: 'rev_02_01',
      authorHandle: 'a_q',
      authorCity: 'Melbourne',
      submittedAt: '2026-03-22',
      rating: 5,
      title: 'said less, meant more.',
      body: 'Roman keeps the room. Architect-brain, not chatter. I asked once and got an honest answer.',
      providerAcknowledged: true,
    },
    {
      id: 'rev_02_02',
      authorHandle: 'p_m',
      authorCity: 'Melbourne',
      submittedAt: '2025-12-30',
      rating: 4,
      body: 'Good company. Bring a book if you want it filled.',
    },
  ],
  vip_03: [
    {
      id: 'rev_03_01',
      authorHandle: 'r_w',
      authorCity: 'Brisbane',
      submittedAt: '2026-01-08',
      rating: 5,
      title: 'worth flying for.',
      body: 'Phoenix tours and the calendar fills. Caught a weekend in town. Easy intro, easy night.',
      providerAcknowledged: true,
    },
    {
      id: 'rev_03_02',
      authorHandle: 'olivia.s',
      authorCity: 'Sydney',
      submittedAt: '2025-11-15',
      rating: 5,
      body: 'Tasteful, attentive, deeply not in a hurry. Will plan around her next tour.',
    },
    {
      id: 'rev_03_03',
      authorHandle: 'c_t',
      authorCity: 'Melbourne',
      submittedAt: '2025-10-02',
      rating: 4,
      title: 'tour-only takes planning.',
      body: 'Worth the wait but expect to plan two weeks out. Once you do, no notes.',
    },
  ],
  vip_04: [
    {
      id: 'rev_04_01',
      authorHandle: 's_k',
      authorCity: 'Melbourne',
      submittedAt: '2026-04-30',
      rating: 5,
      title: 'studio felt like a retreat.',
      body: 'Sebastian runs a calm room. Strong hands, deliberate pace. Walked out lighter.',
      providerAcknowledged: true,
    },
    {
      id: 'rev_04_02',
      authorHandle: 't_h',
      authorCity: 'Melbourne',
      submittedAt: '2026-03-12',
      rating: 5,
      body: 'Best ninety minutes of the week. Books up fast — plan ahead.',
    },
  ],
  vip_05: [
    {
      id: 'rev_05_01',
      authorHandle: 'n_b',
      authorCity: 'Sydney',
      submittedAt: '2026-03-05',
      rating: 5,
      title: 'naples on a tuesday.',
      body: 'Old-world charm without the act. Long evening, easy laughter. Will absolutely return.',
      providerAcknowledged: true,
    },
    {
      id: 'rev_05_02',
      authorHandle: 'r.h',
      authorCity: 'Sydney',
      submittedAt: '2025-12-21',
      rating: 4,
      body: 'Great food choice, better company. Verification pending shouldn’t scare you off.',
    },
  ],
  vip_06: [
    {
      id: 'rev_06_01',
      authorHandle: 'h_v',
      authorCity: 'Melbourne',
      submittedAt: '2025-11-08',
      rating: 5,
      title: 'rare, and worth it.',
      body: 'Iris is selective for a reason. When she says yes the room you’re in changes.',
      providerAcknowledged: true,
    },
    {
      id: 'rev_06_02',
      authorHandle: 'm_d',
      authorCity: 'Melbourne',
      submittedAt: '2025-09-14',
      rating: 5,
      body: 'Pre-screen properly. Don’t ramble. She’ll meet you halfway and then some.',
    },
  ],
  vip_07: [
    {
      id: 'rev_07_01',
      authorHandle: 'e_l',
      authorCity: 'Melbourne',
      submittedAt: '2026-04-19',
      rating: 5,
      title: 'banquette night.',
      body: 'Nico picked the room. I didn’t check my phone for three hours. That’s the review.',
      providerAcknowledged: true,
    },
    {
      id: 'rev_07_02',
      authorHandle: 'j_o',
      authorCity: 'Geelong',
      submittedAt: '2026-02-26',
      rating: 4,
      body: 'Easy intro, good listener, not in a hurry. Recommended.',
    },
    {
      id: 'rev_07_03',
      authorHandle: 'a_p',
      authorCity: 'Melbourne',
      submittedAt: '2025-12-09',
      rating: 5,
      body: 'Standing drink became a long dinner became a longer walk. No notes.',
    },
  ],
  vip_08: [
    {
      id: 'rev_08_01',
      authorHandle: 'g_w',
      authorCity: 'Melbourne',
      submittedAt: '2026-05-04',
      rating: 5,
      title: 'vinyl + late espresso.',
      body: 'Camille made my Thursday night plans for me and I’m grateful. Charming without trying.',
    },
    {
      id: 'rev_08_02',
      authorHandle: 'l_b',
      authorCity: 'Melbourne',
      submittedAt: '2026-03-18',
      rating: 5,
      title: 'on her off-night.',
      body: 'Caught a free Wednesday. Beautiful conversationalist. Worth chasing her calendar.',
      providerAcknowledged: true,
    },
  ],
  vip_09: [
    {
      id: 'rev_09_01',
      authorHandle: 'p_s',
      authorCity: 'Melbourne',
      submittedAt: '2026-04-08',
      rating: 5,
      title: 'fixed what an hour at the gym broke.',
      body: 'Sports-massage background does the work. Strong, slow, no nonsense. Booked again on the spot.',
      providerAcknowledged: true,
    },
    {
      id: 'rev_09_02',
      authorHandle: 'h_y',
      authorCity: 'Melbourne',
      submittedAt: '2026-01-24',
      rating: 5,
      body: 'CBD studio is easy to find. Lift code arrives on time. Hour disappears.',
    },
    {
      id: 'rev_09_03',
      authorHandle: 'k_m',
      authorCity: 'Melbourne',
      submittedAt: '2025-11-30',
      rating: 4,
      body: 'Studio only — be ready for that. Otherwise the table is the table you want.',
    },
  ],
  vip_10: [
    {
      id: 'rev_10_01',
      authorHandle: 'r_e',
      authorCity: 'Melbourne',
      submittedAt: '2026-03-29',
      rating: 5,
      title: 'in for the weekend.',
      body: 'Ash is in town every other Friday and the diary closes fast. Worth the planning.',
    },
    {
      id: 'rev_10_02',
      authorHandle: 's_p',
      authorCity: 'Melbourne',
      submittedAt: '2025-12-12',
      rating: 4,
      body: 'Discreet, sharp, didn’t over-promise. Verification pending; would still book.',
    },
  ],
  vip_11: [
    {
      id: 'rev_11_01',
      authorHandle: 'b_n',
      authorCity: 'Melbourne',
      submittedAt: '2026-04-22',
      rating: 5,
      title: 'two for the table.',
      body: 'Long evening with Lev and Ari. Both fully present, no awkward shuffling. Would absolutely book again.',
      providerAcknowledged: true,
    },
    {
      id: 'rev_11_02',
      authorHandle: 'm_t',
      authorCity: 'Melbourne',
      submittedAt: '2026-02-04',
      rating: 5,
      body: 'Verified duo and it shows. Worth the duo rate.',
    },
  ],
  vip_12: [
    {
      id: 'rev_12_01',
      authorHandle: 'a_v',
      authorCity: 'Melbourne',
      submittedAt: '2026-04-02',
      rating: 5,
      title: 'opera, then somewhere darker.',
      body: 'Juliette is the version of an evening you write about months later. Conversation earned every minute.',
      providerAcknowledged: true,
    },
    {
      id: 'rev_12_02',
      authorHandle: 'r_y',
      authorCity: 'Sydney',
      submittedAt: '2026-01-17',
      rating: 5,
      body: 'Old hotel, late dinner, slow walk home. References are not optional — bring one. Worth it.',
    },
    {
      id: 'rev_12_03',
      authorHandle: 'h_c',
      authorCity: 'Melbourne',
      submittedAt: '2025-11-25',
      rating: 4,
      body: 'A real conversationalist. Bring something to say.',
    },
  ],
};
